import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';

// 定义校历类型
interface Calendar {
    id: string;
    title: string;
    titleEn: string;
    cover: string;
    description: string;
    descriptionEn: string;
    uploadDate: Date;
    status: 'active' | 'draft' | 'archived';
    pdfUrl: string;
}

const CalendarPage: React.FC = () => {
    const { t, i18n } = useTranslation();
    const [calendars, setCalendars] = useState<Calendar[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const isEnglish = i18n.language === 'en';

    // 获取校历数据
    useEffect(() => {
        const fetchCalendars = async () => {
            try {
                const q = query(
                    collection(db, 'calendars'),
                    orderBy('uploadDate', 'desc')
                );
                const querySnapshot = await getDocs(q);
                const calendarData: Calendar[] = [];
                
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    calendarData.push({
                        id: doc.id,
                        title: data.title,
                        titleEn: data.titleEn,
                        cover: data.cover,
                        description: data.description,
                        descriptionEn: data.descriptionEn,
                        uploadDate: data.uploadDate.toDate(),
                        status: data.status,
                        pdfUrl: data.pdfUrl
                    });
                });

                setCalendars(calendarData);
                setLoading(false);
            } catch (err) {
                console.error('Error fetching calendars:', err);
                setError(t('calendar.error.fetch', '获取校历数据失败'));
                setLoading(false);
            }
        };

        fetchCalendars();
    }, [t]);

    // 获取状态标签的样式
    const getStatusStyle = (status: string) => {
        switch(status) {
            case 'active':
                return 'bg-green-100 text-green-800 border-green-300';
            case 'draft':
                return 'bg-yellow-100 text-yellow-800 border-yellow-300';
            case 'archived':
                return 'bg-gray-100 text-gray-800 border-gray-300';
            default:
                return 'bg-gray-100 text-gray-800 border-gray-300';
        }
    };

    // 获取状态的显示文本
    const getStatusText = (status: string) => {
        switch(status) {
            case 'active':
                return t('calendar.status.active', '已发布');
            case 'draft':
                return t('calendar.status.draft', '草稿');
            case 'archived':
                return t('calendar.status.archived', '已归档');
            default:
                return status;
        }
    };

    return (
        <div className="bg-white mt-32">
            {/* 页面标题区域 */}
            <div className="bg-gradient-to-r from-blue-800 to-indigo-900 py-16 text-center">
                <motion.h1 
                    className="text-4xl font-bold text-white mb-4"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {t('calendar.title', '校历')}
                </motion.h1>
                <motion.p 
                    className="text-xl text-blue-100 max-w-3xl mx-auto px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5, delay: 0.2 }}
                >
                    {t('calendar.subtitle', '万湖书院学年校历')}
                </motion.p>
            </div>

            {/* 主要内容区域 */}
            <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : calendars.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <p className="text-gray-500 text-lg">
                            {t('calendar.noCalendars', '暂无校历')}
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-8">
                        {calendars.map((calendar) => (
                            <motion.div
                                key={calendar.id}
                                className="bg-white rounded-lg shadow-lg overflow-hidden"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                            >
                                <div className="md:flex">
                                    <div className="md:flex-shrink-0">
                                        <img
                                            className="h-48 w-full object-cover md:h-full md:w-48"
                                            src={calendar.cover}
                                            alt={isEnglish ? calendar.titleEn : calendar.title}
                                        />
                                    </div>
                                    <div className="p-8 flex-1">
                                        <div className="flex items-center justify-between mb-2">
                                            <h2 className="text-2xl font-bold text-gray-900">
                                                {isEnglish ? calendar.titleEn : calendar.title}
                                            </h2>
                                            <span className={`px-3 py-1 rounded-full text-sm border ${getStatusStyle(calendar.status)}`}>
                                                {getStatusText(calendar.status)}
                                            </span>
                                        </div>
                                        <p className="text-gray-600 mb-4">
                                            {isEnglish ? calendar.descriptionEn : calendar.description}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-gray-500">
                                                {t('calendar.uploadDate', '上传日期')}: {calendar.uploadDate.toLocaleDateString()}
                                            </p>
                                            <a
                                                href={calendar.pdfUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-800 hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                                </svg>
                                                {t('calendar.download', '下载校历')}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CalendarPage; 